// src/utils/notification/notificationCache.js

/**
 * Notification caching utility with 5-minute expiration and cache invalidation
 */

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
const CACHE_KEY_PREFIX = "notification_cache_";
const CACHE_METADATA_KEY = "notification_cache_metadata";

/**
 * Cache metadata structure
 * @typedef {Object} CacheMetadata
 * @property {number} timestamp - When the cache was created
 * @property {string} userId - User ID for cache isolation
 * @property {number} version - Cache version for invalidation
 */

/**
 * Cache entry structure
 * @typedef {Object} CacheEntry
 * @property {any} data - Cached data
 * @property {number} timestamp - When the entry was cached
 * @property {number} expiry - When the entry expires
 * @property {string} key - Cache key
 */

class NotificationCache {
  constructor() {
    this.memoryCache = new Map();
    this.cacheVersion = 1;
    this.userId = null;
  }

  /**
   * Set the current user ID for cache isolation
   * @param {string} userId - Current user ID
   */
  setUserId(userId) {
    if (this.userId !== userId) {
      this.userId = userId;
      this.clearAllCache(); // Clear cache when user changes
    }
  }

  /**
   * Generate a cache key with user isolation
   * @param {string} key - Base cache key
   * @returns {string} Full cache key with user prefix
   */
  getCacheKey(key) {
    return `${CACHE_KEY_PREFIX}${this.userId || "anonymous"}_${key}`;
  }

  /**
   * Get cache metadata
   * @returns {CacheMetadata|null} Cache metadata or null if not found
   */
  getCacheMetadata() {
    try {
      const metadata = localStorage.getItem(CACHE_METADATA_KEY);
      return metadata ? JSON.parse(metadata) : null;
    } catch (error) {
      console.warn("Error reading cache metadata:", error);
      return null;
    }
  }

  /**
   * Set cache metadata
   * @param {CacheMetadata} metadata - Cache metadata to store
   */
  setCacheMetadata(metadata) {
    try {
      localStorage.setItem(CACHE_METADATA_KEY, JSON.stringify(metadata));
    } catch (error) {
      console.warn("Error storing cache metadata:", error);
    }
  }

  /**
   * Check if cache is valid based on version and user
   * @returns {boolean} Whether the cache is valid
   */
  isCacheValid() {
    const metadata = this.getCacheMetadata();
    if (!metadata) return false;

    return (
      metadata.userId === this.userId &&
      metadata.version === this.cacheVersion &&
      Date.now() - metadata.timestamp < CACHE_DURATION
    );
  }

  /**
   * Store data in cache with expiration
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   * @param {number} [customDuration] - Custom cache duration in milliseconds
   * @returns {boolean} Whether the data was successfully cached
   */
  set(key, data, customDuration = CACHE_DURATION) {
    const cacheKey = this.getCacheKey(key);
    const now = Date.now();
    const expiry = now + customDuration;

    const entry = {
      data,
      timestamp: now,
      expiry,
      key: cacheKey,
    };

    try {
      // Store in memory cache
      this.memoryCache.set(cacheKey, entry);

      // Store in localStorage for persistence
      localStorage.setItem(cacheKey, JSON.stringify(entry));

      // Update cache metadata
      this.setCacheMetadata({
        timestamp: now,
        userId: this.userId,
        version: this.cacheVersion,
      });

      return true;
    } catch (error) {
      console.warn("Error storing cache entry:", error);
      // If localStorage is full, try to clear old entries
      this.clearExpiredEntries();
      return false;
    }
  }

  /**
   * Retrieve data from cache
   * @param {string} key - Cache key
   * @returns {any|null} Cached data or null if not found/expired
   */
  get(key) {
    const cacheKey = this.getCacheKey(key);

    // Check memory cache first
    let entry = this.memoryCache.get(cacheKey);

    // If not in memory, check localStorage
    if (!entry) {
      try {
        const stored = localStorage.getItem(cacheKey);
        if (stored) {
          entry = JSON.parse(stored);
          // Restore to memory cache
          this.memoryCache.set(cacheKey, entry);
        }
      } catch (error) {
        console.warn("Error reading cache entry:", error);
        return null;
      }
    }

    if (!entry) return null;

    // Check if entry is expired
    if (Date.now() > entry.expiry) {
      this.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Delete a specific cache entry
   * @param {string} key - Cache key to delete
   */
  delete(key) {
    const cacheKey = this.getCacheKey(key);

    // Remove from memory cache
    this.memoryCache.delete(cacheKey);

    // Remove from localStorage
    try {
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.warn("Error deleting cache entry:", error);
    }
  }

  /**
   * Check if a cache entry exists and is not expired
   * @param {string} key - Cache key to check
   * @returns {boolean} Whether the entry exists and is valid
   */
  has(key) {
    return this.get(key) !== null;
  }

  /**
   * Clear all cache entries for the current user
   */
  clearUserCache() {
    const prefix = this.getCacheKey("");

    // Clear memory cache
    for (const key of this.memoryCache.keys()) {
      if (key.startsWith(prefix)) {
        this.memoryCache.delete(key);
      }
    }

    // Clear localStorage entries
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch (error) {
      console.warn("Error clearing user cache:", error);
    }
  }

  /**
   * Clear all cache entries (all users)
   */
  clearAllCache() {
    // Clear memory cache
    this.memoryCache.clear();

    // Clear localStorage entries
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (
          key &&
          (key.startsWith(CACHE_KEY_PREFIX) || key === CACHE_METADATA_KEY)
        ) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch (error) {
      console.warn("Error clearing all cache:", error);
    }
  }

  /**
   * Clear expired cache entries
   */
  clearExpiredEntries() {
    const now = Date.now();

    // Clear expired memory cache entries
    for (const [key, entry] of this.memoryCache.entries()) {
      if (now > entry.expiry) {
        this.memoryCache.delete(key);
      }
    }

    // Clear expired localStorage entries
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_KEY_PREFIX)) {
          try {
            const entry = JSON.parse(localStorage.getItem(key));
            if (entry && now > entry.expiry) {
              keysToRemove.push(key);
            }
          } catch (error) {
            // Invalid entry, remove it
            keysToRemove.push(key);
          }
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch (error) {
      console.warn("Error clearing expired entries:", error);
    }
  }

  /**
   * Invalidate cache (increment version)
   */
  invalidateCache() {
    this.cacheVersion += 1;
    this.clearUserCache();

    // Update metadata with new version
    this.setCacheMetadata({
      timestamp: Date.now(),
      userId: this.userId,
      version: this.cacheVersion,
    });
  }

  /**
   * Get cache statistics
   * @returns {Object} Cache statistics
   */
  getStats() {
    const memorySize = this.memoryCache.size;
    let localStorageSize = 0;
    let expiredCount = 0;
    const now = Date.now();

    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_KEY_PREFIX)) {
          localStorageSize++;
          try {
            const entry = JSON.parse(localStorage.getItem(key));
            if (entry && now > entry.expiry) {
              expiredCount++;
            }
          } catch (error) {
            expiredCount++;
          }
        }
      }
    } catch (error) {
      console.warn("Error calculating cache stats:", error);
    }

    return {
      memorySize,
      localStorageSize,
      expiredCount,
      cacheVersion: this.cacheVersion,
      userId: this.userId,
      isValid: this.isCacheValid(),
    };
  }
}

// Create singleton instance
const notificationCache = new NotificationCache();

export default notificationCache;

// Export specific cache keys for consistency
export const CACHE_KEYS = {
  NOTIFICATIONS: "notifications",
  UNREAD_COUNT: "unread_count",
  PREFERENCES: "preferences",
  NOTIFICATION_DETAIL: "notification_detail",
};

/**
 * Helper function to cache notifications with pagination info
 * @param {Array} notifications - Notifications array
 * @param {Object} pagination - Pagination info
 * @param {Object} filters - Applied filters
 */
export const cacheNotifications = (notifications, pagination, filters = {}) => {
  const cacheKey = `${CACHE_KEYS.NOTIFICATIONS}_${JSON.stringify(
    filters
  )}_page_${pagination.page}`;
  return notificationCache.set(cacheKey, {
    notifications,
    pagination,
    filters,
  });
};

/**
 * Helper function to get cached notifications
 * @param {Object} filters - Applied filters
 * @param {number} page - Page number
 * @returns {Object|null} Cached notifications data or null
 */
export const getCachedNotifications = (filters = {}, page = 1) => {
  const cacheKey = `${CACHE_KEYS.NOTIFICATIONS}_${JSON.stringify(
    filters
  )}_page_${page}`;
  return notificationCache.get(cacheKey);
};

/**
 * Helper function to invalidate notifications cache on user actions
 */
export const invalidateNotificationsCache = () => {
  // Clear all notification-related cache entries
  const keysToInvalidate = [CACHE_KEYS.NOTIFICATIONS, CACHE_KEYS.UNREAD_COUNT];

  keysToInvalidate.forEach((key) => {
    // Delete entries that start with the key
    const prefix = notificationCache.getCacheKey(key);
    for (const cacheKey of notificationCache.memoryCache.keys()) {
      if (cacheKey.startsWith(prefix)) {
        notificationCache.memoryCache.delete(cacheKey);
      }
    }
  });

  // Also clear from localStorage
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const storageKey = localStorage.key(i);
      if (storageKey) {
        keysToInvalidate.forEach((key) => {
          const prefix = notificationCache.getCacheKey(key);
          if (storageKey.startsWith(prefix)) {
            keysToRemove.push(storageKey);
          }
        });
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.warn("Error invalidating cache from localStorage:", error);
  }
};
